import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Trash2, Edit2 } from "lucide-react";

const emptyPolicy = {
  name: "", description: "", priority: 100, effect: "allow", enabled: true,
  conditions: {
    roles: [], resource_sensitivity: [], resource_types: [],
    allowed_sites: [], allowed_countries: [],
    suspicious_countries: [], denied_countries: [],
    require_managed_device: false, require_geo_permission: false,
    risk_max: null,
    device_required: { os_updated: false, antivirus: false, disk_encryption: false, trusted_network: false },
  },
};

const ROLES = ["chercheur", "medecin", "admin", "auditeur", "partenaire"];
const SENS = ["public", "internal", "confidential", "secret"];
const TYPES = ["medical_record", "clinical_trial", "dataset", "internal_app", "storage"];

export default function PoliciesPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    const { data } = await api.get("/policies");
    setPolicies(data);
  };

  useEffect(() => { load(); }, [user?.mode]);

  const submit = async () => {
    try {
      const payload = JSON.parse(JSON.stringify(editing));
      // Clean device_required: keep only keys set to true
      const dev = payload.conditions.device_required || {};
      const cleanDev = {};
      Object.entries(dev).forEach(([k, v]) => { if (v === true) cleanDev[k] = true; });
      payload.conditions.device_required = Object.keys(cleanDev).length ? cleanDev : null;
      // Convert empty arrays to null so backend match_condition skips them
      ["roles", "resource_sensitivity", "resource_types",
       "allowed_countries", "suspicious_countries", "denied_countries",
       "allowed_sites"].forEach((k) => {
        if (Array.isArray(payload.conditions[k]) && payload.conditions[k].length === 0) {
          payload.conditions[k] = null;
        }
      });
      if (!payload.conditions.require_managed_device) payload.conditions.require_managed_device = null;
      if (!payload.conditions.require_geo_permission) payload.conditions.require_geo_permission = null;

      if (editing._id) {
        await api.put(`/policies/${editing._id}`, payload);
        toast.success("Politique mise à jour");
      } else {
        await api.post("/policies", payload);
        toast.success("Politique créée");
      }
      setOpen(false); setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Supprimer "${p.name}" ?`)) return;
    await api.delete(`/policies/${p._id}`);
    toast.success("Supprimée");
    load();
  };

  const toggleArray = (obj, key, value) => {
    const arr = new Set(obj[key] || []);
    if (arr.has(value)) arr.delete(value); else arr.add(value);
    return { ...obj, [key]: [...arr] };
  };

  return (
    <div className="space-y-6" data-testid="policies-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">
            {t("policies")}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">{t("policy_editor")}</h1>
        </div>
        {isAdmin && (
          <Button
            data-testid="new-policy-btn"
            onClick={() => { setEditing({ ...emptyPolicy }); setOpen(true); }}
            className="bg-cyan-500 text-black hover:bg-cyan-400"
          >
            <Plus className="h-4 w-4" /> {t("new_policy")}
          </Button>
        )}
      </div>

      <div className="grid gap-3">
        {policies.map((p) => (
          <Card key={p._id} className="bg-white/[0.02] border-white/10 rounded-sm p-4"
                data-testid={`policy-row-${p._id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <StatusBadge status={p.effect}>{p.effect}</StatusBadge>
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">
                    prio {p.priority} · v{p.version || 1}
                  </span>
                  {!p.enabled && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">
                      · désactivée
                    </span>
                  )}
                </div>
                <div className="mt-1 text-sm font-semibold">{p.name}</div>
                <div className="text-xs text-white/50 mt-0.5">{p.description}</div>
                <div className="mt-2 flex flex-wrap gap-1 font-mono text-[10px]">
                  {p.conditions?.roles?.map((r) => (
                    <span key={r} className="border border-cyan-500/30 text-cyan-300 px-1.5 py-0.5 rounded-sm">
                      role:{r}
                    </span>
                  ))}
                  {p.conditions?.resource_sensitivity?.map((s) => (
                    <span key={s} className="border border-orange-500/30 text-orange-300 px-1.5 py-0.5 rounded-sm">
                      sens:{s}
                    </span>
                  ))}
                  {p.conditions?.resource_types?.map((s) => (
                    <span key={s} className="border border-emerald-500/30 text-emerald-300 px-1.5 py-0.5 rounded-sm">
                      type:{s}
                    </span>
                  ))}
                  {p.conditions?.suspicious_countries?.map((c) => (
                    <span key={c} className="border border-orange-500/40 text-orange-300 px-1.5 py-0.5 rounded-sm">
                      suspect:{c}
                    </span>
                  ))}
                  {p.conditions?.denied_countries?.map((c) => (
                    <span key={c} className="border border-rose-500/40 text-rose-300 px-1.5 py-0.5 rounded-sm">
                      denied:{c}
                    </span>
                  ))}
                  {p.conditions?.require_managed_device && (
                    <span className="border border-cyan-500/40 text-cyan-300 px-1.5 py-0.5 rounded-sm">
                      managed_device
                    </span>
                  )}
                  {p.conditions?.require_geo_permission && (
                    <span className="border border-cyan-500/40 text-cyan-300 px-1.5 py-0.5 rounded-sm">
                      geo_permission
                    </span>
                  )}
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon"
                          data-testid={`edit-policy-${p._id}`}
                          onClick={() => { setEditing(JSON.parse(JSON.stringify(p))); setOpen(true); }}
                          className="text-white/60 hover:text-cyan-400 hover:bg-white/5 h-8 w-8">
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon"
                          data-testid={`delete-policy-${p._id}`}
                          onClick={() => del(p)}
                          className="text-white/60 hover:text-rose-400 hover:bg-white/5 h-8 w-8">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto"
                       data-testid="policy-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editing?._id ? "Modifier la politique" : "Nouvelle politique"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-[0.2em]">Nom</Label>
                  <Input value={editing.name}
                         onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                         data-testid="policy-name"
                         className="bg-white/[0.03] border-white/10" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-[0.2em]">Priorité (bas = évalué en premier)</Label>
                  <Input type="number" value={editing.priority}
                         onChange={(e) => setEditing({ ...editing, priority: parseInt(e.target.value || 100) })}
                         data-testid="policy-priority"
                         className="bg-white/[0.03] border-white/10 font-mono" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-[0.2em]">Description</Label>
                <Textarea value={editing.description}
                          onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                          data-testid="policy-desc"
                          className="bg-white/[0.03] border-white/10 min-h-[60px]" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-[0.2em]">Effet</Label>
                  <Select value={editing.effect}
                          onValueChange={(v) => setEditing({ ...editing, effect: v })}>
                    <SelectTrigger data-testid="policy-effect" className="bg-white/[0.03] border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="allow">Allow</SelectItem>
                      <SelectItem value="deny">Deny</SelectItem>
                      <SelectItem value="mfa">MFA additionnelle</SelectItem>
                      <SelectItem value="jit">JIT (10 min)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3 mt-6">
                  <Label className="text-[10px] uppercase tracking-[0.2em]">Activée</Label>
                  <Switch checked={editing.enabled}
                          data-testid="policy-enabled"
                          onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} />
                </div>
              </div>

              <PolicyChips label="Rôles" values={ROLES} selected={editing.conditions.roles || []}
                onToggle={(v) => setEditing({ ...editing, conditions: toggleArray(editing.conditions, "roles", v) })} />
              <PolicyChips label="Sensibilité" values={SENS} selected={editing.conditions.resource_sensitivity || []}
                onToggle={(v) => setEditing({ ...editing, conditions: toggleArray(editing.conditions, "resource_sensitivity", v) })} />
              <PolicyChips label="Types de ressource" values={TYPES} selected={editing.conditions.resource_types || []}
                onToggle={(v) => setEditing({ ...editing, conditions: toggleArray(editing.conditions, "resource_types", v) })} />

              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-[0.2em]">Posture terminal requise</Label>
                <div className="grid grid-cols-2 gap-2">
                  {["os_updated", "antivirus", "disk_encryption", "trusted_network"].map((k) => (
                    <div key={k} className="flex items-center justify-between border border-white/10 rounded-sm px-3 py-1.5">
                      <span className="text-xs font-mono">{k}</span>
                      <Switch
                        checked={editing.conditions.device_required?.[k] === true}
                        onCheckedChange={(v) => setEditing({
                          ...editing,
                          conditions: {
                            ...editing.conditions,
                            device_required: { ...(editing.conditions.device_required || {}), [k]: v },
                          },
                        })}
                        data-testid={`policy-device-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-[0.2em]">Pays autorisés (codes ISO, ex: CH,KE,BR)</Label>
                  <Input
                    value={(editing.conditions.allowed_countries || []).join(",")}
                    onChange={(e) => setEditing({
                      ...editing,
                      conditions: {
                        ...editing.conditions,
                        allowed_countries: e.target.value.split(",").map(s => s.trim().toUpperCase()).filter(Boolean),
                      },
                    })}
                    data-testid="policy-allowed-countries"
                    className="bg-white/[0.03] border-white/10 font-mono" placeholder="CH,KE,BR" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-[0.2em]">Pays suspects (à flaguer)</Label>
                  <Input
                    value={(editing.conditions.suspicious_countries || []).join(",")}
                    onChange={(e) => setEditing({
                      ...editing,
                      conditions: {
                        ...editing.conditions,
                        suspicious_countries: e.target.value.split(",").map(s => s.trim().toUpperCase()).filter(Boolean),
                      },
                    })}
                    data-testid="policy-suspicious-countries"
                    className="bg-white/[0.03] border-orange-500/30 font-mono" placeholder="RU,KP,IR" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-[0.2em]">Pays interdits (deny)</Label>
                  <Input
                    value={(editing.conditions.denied_countries || []).join(",")}
                    onChange={(e) => setEditing({
                      ...editing,
                      conditions: {
                        ...editing.conditions,
                        denied_countries: e.target.value.split(",").map(s => s.trim().toUpperCase()).filter(Boolean),
                      },
                    })}
                    data-testid="policy-denied-countries"
                    className="bg-white/[0.03] border-rose-500/30 font-mono" placeholder="XX,YY" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-[0.2em]">Score de risque max</Label>
                  <Input type="number" value={editing.conditions.risk_max ?? ""}
                         onChange={(e) => setEditing({
                           ...editing,
                           conditions: { ...editing.conditions,
                             risk_max: e.target.value === "" ? null : parseInt(e.target.value) },
                         })}
                         data-testid="policy-risk-max"
                         className="bg-white/[0.03] border-white/10 font-mono" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between border border-white/10 rounded-sm px-3 py-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em]">Terminal géré par l'organisation requis</Label>
                  <Switch
                    checked={!!editing.conditions.require_managed_device}
                    onCheckedChange={(v) => setEditing({
                      ...editing,
                      conditions: { ...editing.conditions, require_managed_device: v },
                    })}
                    data-testid="policy-require-managed"
                  />
                </div>
                <div className="flex items-center justify-between border border-white/10 rounded-sm px-3 py-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em]">Consentement géolocalisation requis</Label>
                  <Switch
                    checked={!!editing.conditions.require_geo_permission}
                    onCheckedChange={(v) => setEditing({
                      ...editing,
                      conditions: { ...editing.conditions, require_geo_permission: v },
                    })}
                    data-testid="policy-require-geo"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
                <Button variant="ghost" onClick={() => setOpen(false)}
                        className="text-white/70 hover:bg-white/5">{t("cancel")}</Button>
                <Button onClick={submit} data-testid="save-policy-btn"
                        className="bg-cyan-500 text-black hover:bg-cyan-400">{t("save")}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PolicyChips({ label, values, selected, onToggle }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-[0.2em]">{label}</Label>
      <div className="flex flex-wrap gap-1">
        {values.map((v) => {
          const active = selected.includes(v);
          return (
            <button key={v} type="button"
                    onClick={() => onToggle(v)}
                    className={
                      "text-xs font-mono px-2 py-1 rounded-sm border transition-colors " +
                      (active
                        ? "border-cyan-500/60 bg-cyan-500/10 text-cyan-300"
                        : "border-white/10 text-white/60 hover:border-white/30")
                    }
            >
              {v}
            </button>
          );
        })}
      </div>
    </div>
  );
}
